import { Typography } from '@mui/material';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { translateKey } from 'src/locales/translate';
import { parseStockQuantity } from 'src/sections/@dashboard/warehouse/utils/clinicalStock';
import { WarehouseProductDetailDto } from 'woofwoof-api';
import { SPAN_FULL } from './medicationFormLayout';
import { MedicationsFormModalValuesProps } from './medicationFormSchema';
import { computeStockIssue } from './stockIssueComputation';
import { stockIssueExplanation } from './stockIssueExplanation';

interface MedicationStockIssueNoteProps {
  product: WarehouseProductDetailDto;
  /** "Ilość" cannot be typed: asking the vet to type it says nothing. */
  locked: boolean;
}

/**
 * #692 rev B: one line under "Ilość" — how the dosing gave the quantity on
 * screen ("Kuracja 1 tabletka × 2 dziennie przez 7 dni = 14 tabletek →
 * 2 op. (20 tabletek)"), or, while the field is empty, why it could not.
 * Nothing once the vet has put their own quantity there, nor while the
 * handover the quantity depends on is still the vet's to give (P7B-R10).
 */
export default function MedicationStockIssueNote({
  product,
  locked,
}: MedicationStockIssueNoteProps): React.ReactElement | null {
  const { watch } = useFormContext<MedicationsFormModalValuesProps>();
  const [
    handover,
    quantity,
    measure,
    dosagePending,
    doseForm,
    quantityValue,
    frequencyType,
    frequencyValue,
    durationType,
    durationValue,
  ] = watch([
    'stockHandover',
    'stockQuantity',
    'stockMeasure',
    'dosagePending',
    'form',
    'quantityValue',
    'frequencyType',
    'frequencyValue',
    'durationType',
    'durationValue',
  ]);
  const issue = computeStockIssue(
    {
      dosagePending,
      form: doseForm,
      quantityValue,
      frequencyType,
      frequencyValue,
      durationType,
      durationValue,
    },
    handover,
    product,
  );
  const taken = parseStockQuantity(quantity);

  if (!handover) return null;
  if (!issue.basis) {
    return taken === null && !locked ? (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={SPAN_FULL}
        data-testid="medication-stock-not-computable"
      >
        {translateKey('warehouse.clinical.issueNotComputable')}
      </Typography>
    ) : null;
  }
  if (taken !== issue.quantity || measure !== issue.measure) return null;
  return (
    <Typography
      variant="body2"
      color="text.secondary"
      sx={SPAN_FULL}
      data-testid="medication-stock-explanation"
    >
      {stockIssueExplanation(issue.basis, product)}
    </Typography>
  );
}
